/**
 * Application Entry Point
 *
 * Shows complete integration of:
 * - Redux Store (global state management)
 * - ThemeProvider (CSS variables + theme switching)
 * - FluentProvider (FluentUI components)
 * - React Router
 * - ErrorBoundary (error reporting)
 * - Telemetry (distributed tracing)
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { FluentProvider } from "@fluentui/react-components";
import { Provider } from "react-redux";
import { AuthProvider } from "./providers/AuthProvider";
import "./index.css"; // This imports variables.css

// Store import
import { store } from "./store";

// Theme imports
import { ThemeProvider, useTheme, getFluentTheme } from "./theme";

// Error boundary, telemetry, and PWA
import { ErrorBoundary } from "./components/ErrorBoundary";
import { telemetry } from "./services/telemetryService";
import "./services/pwaInstall"; // Capture install prompt early
import { InstallPrompt } from "./components/InstallPrompt";

// Page imports
import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import Profile from "./pages/profile/Profile";
import NoPage from "./pages/nopage/NoPage";
import Feedback from "./pages/feedback";
import { brand } from "@brand";
import Install from "./pages/install/Install";

// =============================================================================
// GLOBAL ERROR HANDLERS
// Set up global error handling for uncaught errors and promise rejections
// =============================================================================

window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
        void telemetry.reportError(error, undefined, {
            source: "window.onerror",
            sourceFile: source,
            line: lineno,
            column: colno
        });
    } else {
        void telemetry.reportError(new Error(typeof message === "string" ? message : "Unknown error"), undefined, {
            source: "window.onerror",
            sourceFile: source,
            line: lineno,
            column: colno
        });
    }
};

window.onunhandledrejection = event => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    void telemetry.reportError(error, undefined, {
        source: "unhandledrejection"
    });
};

// =============================================================================
// ROUTER CONFIGURATION
// =============================================================================

const router = createHashRouter(
    [
        {
            path: "/",
            element: <Layout />,
            children: [
                {
                    index: true,
                    element: <Chat />
                },
                {
                    path: "/about",
                    element: <brand.about.Page />
                },
                ...(brand.features.installApp
                    ? [
                          {
                              path: "/install",
                              element: <Install />
                          }
                      ]
                    : []),
                {
                    path: "/terms_of_use",
                    element: <brand.pages.TermsOfUse />
                },
                {
                    path: "/privacy_policy",
                    element: <brand.pages.PrivacyPolicy />
                },
                ...(brand.features.smsTerms && brand.pages.SmsTerms
                    ? [
                          {
                              path: "/sms_terms",
                              element: <brand.pages.SmsTerms />
                          }
                      ]
                    : []),
                {
                    path: "/profile",
                    element: <Profile />
                },
                {
                    path: "/feedback",
                    element: <Feedback />
                },
                {
                    path: "*",
                    element: <NoPage />
                }
            ]
        }
    ]
);

// =============================================================================
// FLUENT WRAPPER
// Wraps content with FluentProvider using the correct theme
// =============================================================================

function FluentWrapper({ children }: { children: React.ReactNode }) {
    const { isDarkMode } = useTheme();

    return (
        <FluentProvider theme={getFluentTheme(isDarkMode)} style={{ background: "transparent", height: "100%" }}>
            {children}
        </FluentProvider>
    );
}

// =============================================================================
// APP ROOT
// =============================================================================

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <ErrorBoundary>
            <Provider store={store}>
                <AuthProvider>
                    <ThemeProvider defaultMode="light">
                        <FluentWrapper>
                            <RouterProvider router={router} />
                            {brand.features.installApp && <InstallPrompt />}
                        </FluentWrapper>
                    </ThemeProvider>
                </AuthProvider>
            </Provider>
        </ErrorBoundary>
    </React.StrictMode>
);
